import { fetcherV2 } from "@gogive/services";
import useSWR, { mutate } from "swr";
import { chatRoutes } from "./chat.route";
import type {
  ChatConversationDetail,
  ChatConversationSummary,
} from "./chat.types";

export function conversationsListSwrKey() {
  return [chatRoutes.list, {}] as [
    string,
    Record<string, string | number> | undefined,
  ];
}

export function conversationSwrKey(conversationId: string) {
  return [chatRoutes.get(conversationId), {}] as [
    string,
    Record<string, string | number> | undefined,
  ];
}

export async function revalidateConversationsList() {
  const key = conversationsListSwrKey();
  return mutate<ChatConversationSummary[]>(
    key,
    () => fetcherV2<ChatConversationSummary[]>(key),
    { revalidate: false },
  );
}

export async function revalidateConversation(conversationId: string) {
  const key = conversationSwrKey(conversationId);
  return mutate<ChatConversationDetail>(
    key,
    () => fetcherV2<ChatConversationDetail>(key),
    { revalidate: false },
  );
}

const CONVERSATION_TITLE_MAX_LEN = 60;
const AUTO_TITLE_MAX_WORDS = 6;

export function deriveConversationTitleFromText(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const words = trimmed.split(/\s+/).filter(Boolean);
  const head = words.slice(0, AUTO_TITLE_MAX_WORDS).join(" ");
  const truncated =
    words.length > AUTO_TITLE_MAX_WORDS ? `${head}…` : head;

  if (truncated.length <= CONVERSATION_TITLE_MAX_LEN) return truncated;
  return `${truncated.slice(0, CONVERSATION_TITLE_MAX_LEN - 1)}…`;
}

function titleFromMessages(
  messages: ChatConversationDetail["messages"],
): string | null {
  for (const message of messages) {
    if (message.role !== "user") continue;
    for (const part of message.parts) {
      if (part.type !== "text") continue;
      const title = deriveConversationTitleFromText(part.text);
      if (title) return title;
    }
  }
  return null;
}

export function upsertConversationListCache(
  summary: ChatConversationSummary,
) {
  return mutate<ChatConversationSummary[]>(
    conversationsListSwrKey(),
    (current) => {
      const list = current ?? [];
      const index = list.findIndex((item) => item.id === summary.id);
      if (index === -1) {
        return [summary, ...list];
      }
      const next = [...list];
      next[index] = { ...next[index], ...summary };
      return next;
    },
    { revalidate: false },
  );
}

export function seedConversationCache(
  conversationId: string,
  messages: ChatConversationDetail["messages"],
  title?: string | null,
) {
  return mutate<ChatConversationDetail>(
    conversationSwrKey(conversationId),
    {
      id: conversationId,
      title: title?.trim() || titleFromMessages(messages),
      updatedAt: new Date().toISOString(),
      messages,
    },
    { revalidate: false },
  );
}

export function useConversations() {
  const { data, error, mutate: refresh } = useSWR(
    conversationsListSwrKey(),
    fetcherV2<ChatConversationSummary[]>,
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      suspense: false,
      shouldRetryOnError: false,
    },
  );

  return {
    conversations: data ?? [],
    refresh,
    isLoading: !error && !data,
    isError: error,
  };
}

export function useConversation(conversationId: string | undefined) {
  const { data, error, mutate: refresh } = useSWR(
    () =>
      conversationId ? conversationSwrKey(conversationId) : undefined,
    fetcherV2<ChatConversationDetail>,
    {
      revalidateIfStale: true,
      revalidateOnMount: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      suspense: false,
      shouldRetryOnError: true,
    },
  );

  return {
    conversation: data,
    refresh,
    isLoading: Boolean(conversationId) && !error && !data,
    isError: error,
  };
}
